import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  MonacoEditorConstructionOptions,
  MonacoEditorLoaderService,
  MonacoStandaloneCodeEditor,
} from '@materia-ui/ngx-monaco-editor';
import { filter, take } from 'rxjs/operators';

import { MONACO_SUPPORTED_LANGUAGES } from './monaco-editor.types';

type MonacoWordWrap = 'off' | 'on' | 'wordWrapColumn' | 'bounded';

export type IMarkerData = monaco.editor.IMarkerData;
export interface IMonacoEditorResponse {
  modelId: string;
  data: string;
}
export interface IMonacoEditorError {
  modelId: string;
  errors: IMarkerData[];
}

@Component({
    selector: 'app-monaco-editor',
    templateUrl: './monaco-editor.component.html',
    styleUrls: ['./monaco-editor.component.scss'],
    standalone: false
})
export class MonacoEditorComponent implements OnInit, OnChanges {
  @ViewChild('editor') editor: MonacoStandaloneCodeEditor;
  @Input() language = MONACO_SUPPORTED_LANGUAGES.SCSS;
  @Input() code: string;
  @Input() size: 'large' | 'big' | 'small' | 'medium' = 'small';
  @Input() disabled = false;
  @Input() wordWrap: MonacoWordWrap = 'off';
  @Input() hideHorizontalScrollbar = false;
  @Input() errors: IMarkerData[] = [];
  @Output() codeChange = new EventEmitter<IMonacoEditorResponse>();
  @Output() errorOutput = new EventEmitter<IMonacoEditorError>();
  @Output() init = new EventEmitter<string>();

  editorOptions: MonacoEditorConstructionOptions;
  private modelId: string;

  private getWrappingOptions(): Partial<MonacoEditorConstructionOptions> {
    if (!this.wordWrap || this.wordWrap === 'off') {
      return {
        wordWrap: 'off',
      };
    }

    // Monaco's wordWrap won't always break very long tokens (e.g. URLs).
    // These break characters make generated previews readable without horizontal scrolling.
    return {
      wordWrap: this.wordWrap,
      wrappingIndent: 'indent',
      wrappingStrategy: 'advanced' as any,
      wordWrapBreakAfterCharacters: '/\\\\?&=,:;.#' as any,
    };
  }

  private getScrollbarOptions(): MonacoEditorConstructionOptions['scrollbar'] {
    return {
      vertical: 'auto',
      horizontal: this.hideHorizontalScrollbar ? 'hidden' : 'auto',
      ...(this.hideHorizontalScrollbar
        ? ({ horizontalScrollbarSize: 0 } as any)
        : null),
    } as any;
  }

  constructor(private monacoLoaderService: MonacoEditorLoaderService) {
    this.monacoLoaderService.isMonacoLoaded$
      .pipe(
        filter((isLoaded) => isLoaded),
        take(1)
      )
      .subscribe(() => {
        monaco.editor.defineTheme('myCustomTheme', {
          base: 'vs', // can also be vs or hc-black
          inherit: true, // can also be false to completely replace the builtin rules
          rules: [
            {
              token: 'comment',
              foreground: 'ffa500',
              fontStyle: 'italic underline',
            },
            { token: 'comment.js', foreground: '008800', fontStyle: 'bold' },
            { token: 'comment.css', foreground: '0000ff' }, // will inherit fontStyle from `comment` above
          ],
          colors: {},
        });
        monaco.editor.setModelMarkers = (_model, _owner, errors) => {
          this.errorOutput.emit({ modelId: _model.id, errors });
        };
      });
  }

  ngOnInit(): void {
    this.editorOptions = {
      theme: 'myCustomTheme',
      language: this.language,
      roundedSelection: true,
      autoIndent: 'full',
      readOnly: this.disabled,
      ...this.getWrappingOptions(),
      scrollbar: this.getScrollbarOptions(),
      ...(this.hideHorizontalScrollbar
        ? ({ scrollBeyondLastColumn: 0 } as any)
        : null),
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.disabled ||
      changes.wordWrap ||
      changes.hideHorizontalScrollbar ||
      changes.language
    ) {
      this.updateEditorOptions();
    }
  }

  editorInit(editor: MonacoStandaloneCodeEditor) {
    this.editor = editor;
    this.modelId = editor.getModel().id;
    this.init.emit(this.modelId);
  }

  onDirChange(event: string) {
    this.codeChange.emit({ modelId: this.modelId, data: event });
  }

  updateEditorOptions() {
    this.editorOptions = {
      ...this.editorOptions,
      readOnly: this.disabled,
      language: this.language,
      ...this.getWrappingOptions(),
      scrollbar: this.getScrollbarOptions(),
      ...(this.hideHorizontalScrollbar
        ? ({ scrollBeyondLastColumn: 0 } as any)
        : null),
    };

    // Some wrappers don't automatically re-apply option changes after init.
    if (this.editor) {
      this.editor.updateOptions(this.editorOptions as any);
    }
  }
}
